import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight, ArrowLeft, CheckCircle2, Globe } from 'lucide-react';

const LoginSelection = () => {
  const navigate = useNavigate();
  const [role, setRole] = useState(null); // No role selected initially
  const [language, setLanguage] = useState('English');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const languages = ['English', 'Hausa', 'Yoruba', 'Igbo'];

  const content = {
    English: {
      back: "Back to Home",
      title: "Login to Your Portal",
      desc: "Select the portal that fits your role to access your personalized dashboard and features.",
      instructor: "Login as Instructor",
      learner: "Login as Learner",
      continue: "Login",
      selectRole: "Please select a portal first"
    },
    Hausa: {
      back: "Koma Gida",
      title: "Shiga Mashigar Ku",
      desc: "Zaɓi mashigar da ta dace da matsayin ku domin samun damar dashboard ɗinku da abubuwan da suka dace.",
      instructor: "Shiga a matsayin Malami",
      learner: "Shiga a matsayin Ɗalibi",
      continue: "Shiga",
      selectRole: "Da fatan zaɓi mashigar ku da farko"
    },
    Yoruba: {
      back: "Padà sí Ilé",
      title: "Wọlé sí Portal Rẹ",
      desc: "Yan portal ti o ba ipa rẹ mu lati wọle si dashboard rẹ ati gbogbo awọn ẹya ara rẹ.",
      instructor: "Wọlé gẹgẹbi Olùkọ́",
      learner: "Wọlé gẹgẹbi Akẹ́kọ̀ọ́",
      continue: "Wọlé",
      selectRole: "Jọwọ yan portal akọkọ"
    },
    Igbo: {
      back: "Laghachi n'Ụlọ",
      title: "Banye na Portal Gị",
      desc: "Họrọ portal dabara na ọrụ gị iji nweta dashboard gị na atụmatụ gị niile.",
      instructor: "Banye dịka Onye Nkuzi",
      learner: "Banye dịka Onye Mmụta",
      continue: "Banye",
      selectRole: "Biko họrọ portal gị mbụ"
    }
  };

  const t = content[language];

  const handleContinue = () => {
    if (!role) {
      alert(t.selectRole);
      return;
    }

    setLoading(true);
    setTimeout(() => {
      if (role === 'learner') navigate('/login');
      else navigate('/tutor/login');
    }, 1200);
  };

  return (
    <>
      {loading && (
        <div className="fixed inset-0 bg-white flex items-center justify-center z-50">
          <div className="w-20 h-20 border-8 border-t-[#1976D2] border-b-gray-200 rounded-full animate-spin" />
        </div>
      )}

      <div className={`min-h-screen w-full bg-white flex flex-col md:flex-row font-sans text-[#1a2b4b] ${loading ? 'pointer-events-none opacity-50' : ''}`}>
        {/* Left Image */}
        <div className="hidden md:block w-1/2 p-8">
          <div className="relative h-full w-full rounded-[3rem] overflow-hidden shadow-[20px_20px_60px_#bebebe,-20px_-20px_60px_#ffffff] group">
            <img
              src="africa-edtech-classroom-tablets.jpg"
              alt="Learning Portal"
              className="absolute inset-0 w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-700"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
          </div>
        </div>

        {/* Right Content */}
        <div className="w-full md:w-1/2 flex flex-col p-8 md:p-16 lg:p-24 relative justify-center">
          {/* Top Nav & Language Selector */}
          <div className="flex justify-between items-center mb-16">
            <button
              onClick={() => navigate('/')}
              className="flex items-center text-[#1a2b4b] font-semibold text-sm hover:-translate-x-1 transition-transform"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t.back}
            </button>

            <div className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center space-x-2 bg-white px-5 py-2.5 rounded-full shadow-[4px_4px_10px_#ddd,-4px_-4px_10px_#fff] transition-all text-sm font-semibold border border-gray-50"
              >
                <Globe className="w-4 h-4 text-[#1976D2]" />
                <span>{language}</span>
              </button>

              {dropdownOpen && (
                <div className="absolute right-0 mt-2 w-40 bg-white rounded-2xl shadow-2xl border border-gray-100 overflow-hidden z-20">
                  {languages.map((lang) => (
                    <button
                      key={lang}
                      onClick={() => { setLanguage(lang); setDropdownOpen(false); }}
                      className={`w-full text-left px-5 py-3 text-sm font-medium transition-colors ${
                        language === lang ? 'text-blue-600 font-bold bg-blue-50/50' : 'text-gray-600 hover:bg-blue-50'
                      }`}
                    >
                      {lang}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Title & Description */}
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-black mb-6 tracking-tight text-gray-900 leading-snug">
            {t.title}
          </h1>
          <p className="text-gray-600 text-base md:text-lg leading-relaxed mb-12 max-w-md font-medium">
            {t.desc}
          </p>

          {/* Portal Selection */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-12">
            {[
              { id: 'instructor', label: t.instructor },
              { id: 'learner', label: t.learner }
            ].map((item) => (
              <button
                key={item.id}
                onClick={() => setRole(item.id)}
                className={`relative flex items-center p-6 rounded-3xl transition-all duration-300 transform ${
                  role === item.id
                    ? 'bg-white shadow-[inset_6px_6px_12px_#e2e2e2,inset_-6px_-6px_12px_#ffffff] scale-[0.98]'
                    : 'bg-white shadow-[10px_10px_20px_#e6e6e6,-10px_-10px_20px_#ffffff] hover:-translate-y-1'
                }`}
              >
                <div className={`w-6 h-6 rounded-full border-2 mr-4 flex items-center justify-center transition-all ${
                  role === item.id ? 'bg-[#1976D2] border-[#1976D2] scale-110' : 'border-gray-300'
                }`}>
                  {role === item.id && <CheckCircle2 className="w-4 h-4 text-white" />}
                </div>
                <span className={`font-semibold text-sm md:text-base ${role === item.id ? 'text-[#1976D2]' : 'text-gray-600'}`}>
                  {item.label}
                </span>
              </button>
            ))}
          </div>

          {/* Login Button */}
          <button
            onClick={handleContinue}
            className={`w-full py-5 rounded-2xl font-bold flex items-center justify-center space-x-3 shadow-[0_10px_20px_rgba(25,118,210,0.3)] transition-all group ${
              role ? 'bg-[#1976D2] text-white hover:shadow-[0_15px_30px_rgba(25,118,210,0.4)] hover:-translate-y-1' : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
            disabled={!role}
          >
            <span className="text-base md:text-lg">{t.continue}</span>
            <ChevronRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" />
          </button>

        </div>
      </div>
    </>
  );
};

export default LoginSelection;